import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { createJob } from '../../services/firestoreService';

const PostJob = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // State for form data
  const [formData, setFormData] = useState({
    title: '',
    company: '',
    location: '',
    type: '',
    salaryMin: '',
    salaryMax: '',
    description: '',
    requirements: [''],
    postedDate: new Date().toISOString().split('T')[0],
    applicationDeadline: '',
    contactEmail: '',
    contactPhone: '',
    featured: false
  });
  
  // State for form validation
  const [errors, setErrors] = useState({});
  
  // Job type options
  const jobTypeOptions = [
    'Full-time',
    'Part-time',
    'Contract',
    'Temporary',
    'Internship',
    'Remote'
  ];
  
  // Handle input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  
  // Handle requirements change
  const handleRequirementChange = (index, value) => {
    const updatedRequirements = [...formData.requirements];
    updatedRequirements[index] = value;
    setFormData({
      ...formData,
      requirements: updatedRequirements
    });
  };
  
  // Add new requirement field
  const addRequirement = () => {
    setFormData({
      ...formData,
      requirements: [...formData.requirements, '']
    });
  };
  
  // Remove requirement field
  const removeRequirement = (index) => {
    const updatedRequirements = [...formData.requirements];
    updatedRequirements.splice(index, 1);
    setFormData({
      ...formData,
      requirements: updatedRequirements
    });
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) newErrors.title = 'Job title is required';
    if (!formData.company.trim()) newErrors.company = 'Company name is required';
    if (!formData.location.trim()) newErrors.location = 'Location is required';
    if (!formData.type) newErrors.type = 'Job type is required';
    if (!formData.description.trim()) newErrors.description = 'Job description is required';
    if (!formData.contactEmail.trim()) newErrors.contactEmail = 'Contact email is required';
    
    // Validate email format
    if (formData.contactEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Please enter a valid email address';
    }
    
    // Validate phone format if provided
    if (formData.contactPhone.trim() && !/^[0-9+\-() ]{10,20}$/.test(formData.contactPhone)) {
      newErrors.contactPhone = 'Please enter a valid phone number';
    }
    
    // Validate salary
    if ((!formData.salaryMin.trim() && formData.salaryMax.trim()) || 
        (formData.salaryMin.trim() && !formData.salaryMax.trim())) {
      newErrors.salary = 'Please provide both minimum and maximum salary or leave both empty';
    } else if (formData.salaryMin.trim() && formData.salaryMax.trim()) {
      if (parseInt(formData.salaryMin) >= parseInt(formData.salaryMax)) {
        newErrors.salary = 'Maximum salary must be greater than minimum salary';
      }
    }
    
    // Validate requirements
    const validRequirements = formData.requirements.filter(req => req.trim() !== '');
    if (validRequirements.length === 0) {
      newErrors.requirements = 'At least one requirement is required';
    }
    
    // Validate application deadline if provided
    if (formData.applicationDeadline) {
      const deadlineDate = new Date(formData.applicationDeadline);
      const today = new Date();
      if (deadlineDate <= today) {
        newErrors.applicationDeadline = 'Application deadline must be in the future';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentUser) {
      alert('Please log in to post a job.');
      return;
    }

    if (validateForm()) {
      setLoading(true);

      try {
        // Format the data for submission
        const jobData = {
          title: formData.title,
          company: formData.company,
          location: formData.location,
          type: formData.type,
          description: formData.description,
          salary: formData.salaryMin && formData.salaryMax ?
            `$${formData.salaryMin.trim()} - $${formData.salaryMax.trim()}` :
            'Not specified',
          requirements: formData.requirements.filter(req => req.trim() !== ''),
          applicationDeadline: formData.applicationDeadline ? new Date(formData.applicationDeadline) : null,
          contactEmail: formData.contactEmail || currentUser.email
        };

        console.log('Creating job with data:', jobData);
        const jobId = await createJob(jobData, currentUser.uid);
        console.log('Job created with ID:', jobId);

        // Show success message and redirect
        alert('Job posted successfully!');
        navigate(`/jobs/${jobId}`);
      } catch (error) {
        console.error('Error creating job:', error);
        alert('Failed to create job posting. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      // Scroll to the first error
      const firstErrorField = Object.keys(errors)[0];
      const errorElement = document.getElementById(firstErrorField);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };
  
  return (
    <div className="bg-white">
      {/* Page Header */}
      <div className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* APN Logo */}
            <div className="flex justify-center mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-white.jpg`}
                alt="APN Logo"
                className="h-16"
              />
            </div>
            <h1 className="text-3xl font-bold">Post a Job</h1>
          </div>
        </div>
      </div>

      {/* Post Job Form */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 p-6">
            <form onSubmit={handleSubmit}>
              {/* Job Title */}
              <div className="mb-6">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                  Job Title *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue ${
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., Investment Banking Associate"
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-500">{errors.title}</p>
                )}
              </div>
              
              {/* Company Name */}
              <div className="mb-6">
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue ${
                    errors.company ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., Goldman Sachs"
                />
                {errors.company && (
                  <p className="mt-1 text-sm text-red-500">{errors.company}</p>
                )}
              </div>
              
              {/* Location and Job Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                    Location *
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue ${
                      errors.location ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., New York, NY or Remote"
                  />
                  {errors.location && (
                    <p className="mt-1 text-sm text-red-500">{errors.location}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-2">
                    Job Type *
                  </label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue ${
                      errors.type ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select Job Type</option>
                    {jobTypeOptions.map((type, index) => (
                      <option key={index} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  {errors.type && (
                    <p className="mt-1 text-sm text-red-500">{errors.type}</p>
                  )}
                </div>
              </div>
              
              {/* Salary Range */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Salary Range (USD) <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <div className="flex items-center space-x-2">
                  <div className="relative flex-1">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">$</span>
                    <input
                      type="number"
                      id="salaryMin"
                      name="salaryMin"
                      value={formData.salaryMin}
                      onChange={handleInputChange}
                      className={`w-full pl-8 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue ${
                        errors.salary ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Min"
                      min="0"
                    />
                  </div>
                  <span className="text-gray-500">to</span>
                  <div className="relative flex-1">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">$</span>
                    <input
                      type="number"
                      id="salaryMax"
                      name="salaryMax"
                      value={formData.salaryMax}
                      onChange={handleInputChange}
                      className={`w-full pl-8 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue ${
                        errors.salary ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Max"
                      min="0"
                    />
                  </div>
                </div>
                {errors.salary && (
                  <p className="mt-1 text-sm text-red-500">{errors.salary}</p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  Leave blank if you prefer not to disclose the salary range.
                </p>
              </div>
              
              {/* Job Description */}
              <div className="mb-6">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Job Description *
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="5"
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue ${
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Provide a detailed description of the job role, responsibilities, and company information..."
                ></textarea>
                {errors.description && (
                  <p className="mt-1 text-sm text-red-500">{errors.description}</p>
                )}
              </div>
              
              {/* Requirements */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Requirements *
                </label>
                {formData.requirements.map((requirement, index) => (
                  <div key={index} className="flex items-center mb-2">
                    <input
                      type="text"
                      value={requirement}
                      onChange={(e) => handleRequirementChange(index, e.target.value)}
                      className={`flex-1 px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue ${
                        errors.requirements ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder={`Requirement ${index + 1}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeRequirement(index)}
                      className="ml-2 text-red-500 hover:text-red-700"
                      disabled={formData.requirements.length === 1}
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addRequirement}
                  className="mt-2 text-apn-blue hover:text-blue-700 flex items-center"
                >
                  <svg className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Requirement
                </button>
                {errors.requirements && (
                  <p className="mt-1 text-sm text-red-500">{errors.requirements}</p>
                )}
              </div>
              
              {/* Application Deadline */}
              <div className="mb-6">
                <label htmlFor="applicationDeadline" className="block text-sm font-medium text-gray-700 mb-2">
                  Application Deadline <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <input
                  type="date"
                  id="applicationDeadline"
                  name="applicationDeadline"
                  value={formData.applicationDeadline}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue ${
                    errors.applicationDeadline ? 'border-red-500' : 'border-gray-300'
                  }`}
                  min={new Date().toISOString().split('T')[0]}
                />
                {errors.applicationDeadline && (
                  <p className="mt-1 text-sm text-red-500">{errors.applicationDeadline}</p>
                )}
              </div>
              
              {/* Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Email *
                  </label>
                  <input
                    type="email"
                    id="contactEmail"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue ${
                      errors.contactEmail ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., careers@company.com"
                  />
                  {errors.contactEmail && (
                    <p className="mt-1 text-sm text-red-500">{errors.contactEmail}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Phone <span className="text-gray-500 text-xs">(Optional)</span>
                  </label>
                  <input
                    type="tel"
                    id="contactPhone"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue ${
                      errors.contactPhone ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., (123) 456-7890"
                  />
                  {errors.contactPhone && (
                    <p className="mt-1 text-sm text-red-500">{errors.contactPhone}</p>
                  )}
                </div>
              </div>
              
              {/* Featured Job */}
              <div className="mb-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="featured"
                    name="featured"
                    checked={formData.featured}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-apn-blue focus:ring-apn-blue border-gray-300 rounded"
                  />
                  <label htmlFor="featured" className="ml-2 block text-sm text-gray-700">
                    Feature this job (additional fee may apply)
                  </label>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Featured jobs appear at the top of the job board and receive more visibility.
                </p>
              </div>
              
              {/* Submit Buttons */}
              <div className="flex justify-end space-x-4">
                <Link
                  to="/jobs"
                  className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  className="px-6 py-2 bg-apn-blue text-white rounded-md hover:bg-blue-700 transition duration-200"
                >
                  Post Job
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PostJob;
